/**
 * Preview-mode handler.
 *
 * Replaces the static placeholder. Listens for postMessage from a
 * narrow allowlist of Studio origins, validates each incoming config the
 * same way the production loader does, and re-renders the quiz via the
 * shared orchestrator. Suppresses analytics emit and the delivery redirect
 * (those are production-only side effects).
 *
 * Wire protocol — final contract for v2.0:
 *
 *   Studio → runtime:
 *     { type: 'QUIZ_PREVIEW_CONFIG_UPDATED', config: <RuntimeQuizConfig> }
 *     { type: 'QUIZ_PREVIEW_RESET' }
 *
 *   runtime → Studio:
 *     { type: 'QUIZ_PREVIEW_READY' }   // sent once on startup, per allowlist origin
 */

import { renderDeliveryUI } from '@/runtime/delivery';
import { renderError } from '@/runtime/errors';
import { validateConfig, type LoaderError } from '@/runtime/loader';
import { mountQuiz } from '@/runtime/render';
import type { RuntimeQuizConfig } from '@/types/runtime';

const READY_MESSAGE = { type: 'QUIZ_PREVIEW_READY' } as const;

function parseAllowlist(raw: string | undefined): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map(normalizeOrigin),
  );
}

// A browser MessageEvent.origin is ALWAYS a bare serialized origin —
// scheme://host[:port], no path, no trailing slash. Env values routinely carry
// a trailing slash (e.g. "https://studio.example.com/") or even a path, which
// would never === ev.origin and would silently refuse EVERY message, blanking
// the preview. Collapse each entry to its bare origin so a stray slash can't
// break the handshake. Falls back to trimming trailing slashes if the entry
// isn't a parseable absolute URL.
function normalizeOrigin(entry: string): string {
  try {
    return new URL(entry).origin;
  } catch {
    return entry.replace(/\/+$/, '');
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function renderHolding(root: HTMLElement): void {
  root.replaceChildren();
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.flexDirection = 'column';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '0.5rem';
  wrap.style.textAlign = 'center';
  wrap.style.padding = '1rem';

  const title = document.createElement('h1');
  title.textContent = 'Preview mode';
  title.style.color = 'var(--quiz-primary, hsl(250, 80%, 60%))';
  title.style.fontSize = '1.5rem';
  title.style.fontWeight = '600';
  title.style.margin = '0';
  wrap.appendChild(title);

  const note = document.createElement('p');
  note.textContent = 'Waiting for preview config from Studio…';
  note.style.margin = '0';
  note.style.opacity = '0.7';
  wrap.appendChild(note);

  root.appendChild(wrap);
}

/**
 * Send QUIZ_PREVIEW_READY back to Studio. Posts ONCE per allowlist origin
 * with an explicit targetOrigin — never `*`. The browser only delivers the
 * message if the parent frame's origin matches that target, so other
 * framers cannot observe the runtime's existence via this handshake.
 *
 * Empty allowlist → log error and skip. Studio can't be wired up without
 * an allowlist anyway, and broadcasting with `*` would leak the runtime
 * to whatever frame happens to host us.
 */
function postReady(target: WindowProxy, allowlist: Set<string>): void {
  if (allowlist.size === 0) {
    console.error(
      '[quiz-runtime] preview: VITE_STUDIO_ORIGIN_ALLOWLIST is empty; not posting QUIZ_PREVIEW_READY. Set the env var to your Studio origin(s).',
    );
    return;
  }
  for (const origin of allowlist) {
    try {
      target.postMessage(READY_MESSAGE, origin);
    } catch (e) {
      console.warn('[quiz-runtime] preview: failed to post READY to', origin, e);
    }
  }
}

export function startPreviewMode(root: HTMLElement): () => void {
  const allowlist = parseAllowlist(import.meta.env.VITE_STUDIO_ORIGIN_ALLOWLIST);
  if (allowlist.size === 0) {
    console.error(
      '[quiz-runtime] preview: VITE_STUDIO_ORIGIN_ALLOWLIST is empty; all incoming messages will be refused. See .env.local.example.',
    );
  }

  // One warn per unique unknown origin so a misconfigured Studio doesn't
  // flood the console.
  const warnedOrigins = new Set<string>();

  let currentAbort: AbortController | null = null;
  let currentMount: Promise<void> | null = null;

  // Mount-or-remount with the same validated config. Extracted so the
  // Restart button on the delivery card can re-enter the same flow
  // without going through validateConfig again — the config in scope
  // already passed validation.
  const mountValidated = (cfg: RuntimeQuizConfig): void => {
    currentAbort?.abort();
    currentAbort = new AbortController();
    const myAbort = currentAbort;
    currentMount = mountQuiz(root, cfg, {
      signal: myAbort.signal,
      emit: () => {}, // analytics suppressed in preview
      onFinalDelivery: (finalConfig, finalRoot) => {
        if (myAbort.signal.aborted) return; // superseded by a newer config
        renderDeliveryUI(finalRoot, {
          url: finalConfig.delivery.url,
          mode: 'preview',
          ctaText: finalConfig.settings.ctaText,
          onRestart: () => mountValidated(finalConfig),
        });
      },
    });
  };

  const handleValid = (config: ReturnType<typeof validateConfig> & { ok: true }): void => {
    mountValidated(config.value);
  };

  const handleError = (error: LoaderError): void => {
    currentAbort?.abort();
    currentAbort = null;
    renderError(root, error);
  };

  const onMessage = (ev: MessageEvent): void => {
    if (!allowlist.has(ev.origin)) {
      if (!warnedOrigins.has(ev.origin)) {
        console.warn('[quiz-runtime] preview: rejected message from origin', ev.origin);
        warnedOrigins.add(ev.origin);
      }
      return;
    }
    const data: unknown = ev.data;
    if (!isObject(data) || typeof data['type'] !== 'string') return;
    const type = data['type'];

    if (type === 'QUIZ_PREVIEW_RESET') {
      currentAbort?.abort();
      currentAbort = null;
      renderHolding(root);
      return;
    }

    if (type !== 'QUIZ_PREVIEW_CONFIG_UPDATED') return;

    const validated = validateConfig(data['config']);
    if (!validated.ok) {
      handleError(validated.error);
      return;
    }
    handleValid(validated);
  };

  window.addEventListener('message', onMessage);
  renderHolding(root);

  // Tell Studio we're listening. Posts AFTER the listener is attached and
  // the holding state is mounted, so Studio's first CONFIG_UPDATED won't
  // race with our setup.
  if (window.parent && window.parent !== window) {
    postReady(window.parent, allowlist);
  } else {
    console.warn('[quiz-runtime] preview: not inside a parent frame; QUIZ_PREVIEW_READY skipped');
  }

  return () => {
    window.removeEventListener('message', onMessage);
    currentAbort?.abort();
    void currentMount; // keep ref so it doesn't get GC'd before abort propagates
  };
}
